import { Injectable } from '@angular/core';
import { UntypedFormGroup, ValidatorFn, Validators, ValidationErrors } from '@angular/forms';
import { BcRestApi } from 'common/api/rest-api';
import { SalesforceForm, SalesforceField, SalesforceFieldTypes, SalesforceReferenceFieldDefaultValue } from '../model/salesforce-form.model';
import { SalesforceFormControl } from '../model/salesforce-form-control';

@Injectable()
export class SalesforceFieldService {
  constructor (private _bcRestApi: BcRestApi) {
  }

  public toFormGroup(salesforceFormData: SalesforceForm): UntypedFormGroup {
    let group: any = {};
    salesforceFormData.sections.forEach(section => {
      section.fields.forEach((field: SalesforceField) => {
        group[field.name] = new SalesforceFormControl(field.value || this._getFieldBackoffValue(field), this._getFieldValidator(field), field);
      });
    });

    Object.keys(group).forEach((key) => {
      const grp: SalesforceFormControl = group[key];
      if (!grp.salesforceField || !grp.salesforceField.controllerName || !group[grp.salesforceField.controllerName]) {
        return;
      }

      grp.setSalesforceController(group[grp.salesforceField.controllerName]);
    });

    return new UntypedFormGroup(group);
  }

  public getSalesforceForm(chatId: string, salesforceObjectType: string): Promise<SalesforceForm> {
    return this._bcRestApi.getSalesforceForm(chatId, salesforceObjectType);
  }

  private _getFieldBackoffValue(field: SalesforceField): string | number | boolean {
    switch (field.type) {
      case SalesforceFieldTypes.REFERENCE:
        return field.value
          ? (<SalesforceReferenceFieldDefaultValue> field.value).id
          : this._getFieldDefaultBackoffValue(field);
      default:
        return field.value
          ? <string | number | boolean> field.value
          : this._getFieldDefaultBackoffValue(field);
    }
  }

  private _getFieldDefaultBackoffValue(field: SalesforceField): string | boolean {
    switch (field.type) {
      case SalesforceFieldTypes.BOOLEAN:
        return false;
      default:
        return '';
    }
  }

  private _getFieldValidator(field: SalesforceField): ValidatorFn[] {
    let validators: ValidatorFn[] = [];

    let typeValidator: ValidatorFn = this._getFieldValidatorByType(field.type);
    if (typeValidator) {
      validators.push(typeValidator);
    }

    if (field.isRequired && !field.isDependentPicklist) {
      validators.push(Validators.required);
    } else if (field.isRequired && field.controllerName) {
      validators.push(SalesforceFieldService.maybeRequired);
    }

    return validators;
  }

  private _getFieldValidatorByType(fieldType: String): ValidatorFn {
    switch (fieldType) {
      case SalesforceFieldTypes.DATE:
        return Validators.pattern('^[0-9]{4}\-(0[1-9]|1[0-2])\-(0[1-9]|[1-2][0-9]|3[0-1])$');
      case SalesforceFieldTypes.TIME:
        return Validators.pattern('^([01][0-9]|2[0-3])\:([0-5][0-9])$');
      case SalesforceFieldTypes.DATETIME:
        return Validators.pattern('^[0-9]{4}\-(0[1-9]|1[0-2])\-(0[1-9]|[1-2][0-9]|3[0-1]) ([01][0-9]|2[0-3]):([0-5][0-9])$');
      default:
        return null;
    }
  }

  private static maybeRequired(control: SalesforceFormControl): ValidationErrors | null {
    if (!control.salesforceController) {
      return null;
    }

    const values = control.getValidControlledValues();
    if (values.length === 0) {
      return null;
    }

    const item = control.getPicklistValue();
    if (item && values.map(e => e.value).indexOf(item.value) >= 0) {
      return null;
    }

    return { required: true };
  }
}
